<template>
    <Overlay :show="showOverlay">
        <template #overlay>
            <div class="text-center">
                <p>{{ overlayMessage }}</p>
                <b-spinner variant="primary" />
            </div>
        </template>
        <validation-observer ref="email">
            <b-form-group
                :label="$t('Component(RequestEmailOptions):label-email')"
                label-for="email"
            >
                <validation-provider
                    #default="{ errors }"
                    name="Email"
                    rules="required|email"
                >
                    <b-form-input
                        id="email"
                        :placeholder="$t('Component(RequestEmailOptions):placeholder-email')"
                        required
                        type="email"
                        @input="onEmailInput"
                        :state="errors.length > 0 ? false:null"
                        v-model="emailValue"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
            </b-form-group>
        </validation-observer>
        <Transition name="fade">
            <div v-if="showProviderSelector">
                <p>{{ $t('Component(RequestEmailOptions):pick-provider-instruction') }}</p>
                <b-form-group
                    :label="$t('Component(RequestEmailOptions):label-email-provider')"
                    label-for="email-provider"
                >
                    <ProviderSelectionList
                        id="email-provider"
                        @selected="onProviderSelect"
                    />
                </b-form-group>
            </div>
        </Transition>
        <validation-observer ref="password">
            <Transition name="fade">
                <b-form-group
                    v-if="showPasswordField"
                    :label="$t('Component(RequestEmailOptions):label-password')"
                    label-for="password"
                >
                    <validation-provider
                        #default="{ errors }"
                        :name="$t('Component(RequestEmailOptions):label-password')"
                        rules="required"
                    >
                        <b-input-group
                            class="input-group-merge"
                            :class="errors.length > 0 ? 'is-invalid':null"
                        >
                            <b-form-input
                                id="password"
                                :state="errors.length > 0 ? false:null"
                                v-model="password"
                                class="form-control-merge"
                                :placeholder="$t('Component(RequestEmailOptions):placeholder-password')"
                                :type="passwordFieldType"
                            />
                            <b-input-group-append is-text>
                                <feather-icon
                                    class="cursor-pointer"
                                    :icon="passwordToggleIcon"
                                    @click="togglePasswordVisibility"
                                />
                            </b-input-group-append>
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                </b-form-group>
            </Transition>
        </validation-observer>
        <validation-observer ref="appPassword">
            <Transition name="fade">
                <b-form-group
                    v-if="showAppPassword"
                    :label="$t('Component(RequestEmailOptions):label-app-password')"
                    label-for="app-password"
                >
                    <validation-provider
                        #default="{ errors }"
                        :name="$t('Component(RequestEmailOptions):label-app-password')"
                        rules="required"
                    >
                        <b-input-group
                            class="input-group-merge"
                            :class="errors.length > 0 ? 'is-invalid':null"
                        >
                            <b-form-input
                                id="app-password"
                                :state="errors.length > 0 ? false:null"
                                v-model="appPassword"
                                class="form-control-merge"
                                :placeholder="$t('Component(RequestEmailOptions):placeholder-app-password')"
                                type="password"
                            />
                            <b-input-group-append is-text>
                                <feather-icon
                                    class="cursor-pointer"
                                    :icon="passwordToggleIcon"
                                    @click="togglePasswordVisibility"
                                />
                            </b-input-group-append>
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                    <b-alert
                        class="mt-1"
                        variant="warning"
                        show
                    >
                        <div class="alert-body">
                            <p>{{ $t('Component(RequestEmailOptions):app-password-instruction', {provider: emailProvider.title}) }}</p>
                            <div class="text-right">
                                <a :href="emailProvider.appPasswordSettings.instructionLink" target="_blank">
                                    {{ $t('Component(RequestEmailOptions):app-password-instruction-link') }}
                                </a>
                            </div>
                        </div>
                    </b-alert>
                </b-form-group>
            </Transition>
        </validation-observer>
        <Transition name="fade">
            <div v-if="showConfigurationExists">
                <div v-if="existingConfiguration.valid">
                    <b-alert
                        class="mt-1"
                        variant="success"
                        show
                    >
                        <div class="alert-body">
                            <p>{{ $t('Component(RequestEmailOptions):configuration-exists-and-valid') }}</p>
                        </div>
                    </b-alert>
                </div>
                <div v-else>
                    <b-alert
                        class="mt-1"
                        variant="warning"
                        show
                    >
                        <div class="alert-body">
                            <p>{{ $t('Component(RequestEmailOptions):configuration-exists-and-needs-to-be-validated') }}</p>
                        </div>
                    </b-alert>
                </div>
            </div>
        </Transition>
        <Transition>
            <b-alert
                v-if="isInvitationFlow"
                class="mt-1"
                variant="warning"
                show
            >
                <div class="alert-body">
                    <p>{{ $t( isInvitationSent ? 'Component(RequestEmailOptions):invitation-sent' : 'Component(RequestEmailOptions):invitation-flow-instruction', {provider: emailProvider.title}) }}</p>
                </div>
            </b-alert>
        </Transition>
        <div v-if="showSmtpSettings">
            <b-form-group
                :label="$t('Component(RequestEmailOptions):label-smtp-server')"
                label-for="host"
            >
                <validation-provider
                    #default="{ errors }"
                    :name="$t('Component(RequestEmailOptions):label-smtp-server')"
                    rules="required"
                >
                    <b-form-input
                        id="host"
                        :state="errors.length > 0 ? false:null"
                        v-model="host"
                        :placeholder="$t('Component(RequestEmailOptions):placeholder-smtp-server')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
            </b-form-group>
            <b-form-group
                :label="$t('Component(RequestEmailOptions):label-smtp-port')"
                label-for="port"
            >
                <validation-provider
                    #default="{ errors }"
                    :name="$t('Component(RequestEmailOptions):label-smtp-port')"
                    rules="required|integer"
                >
                    <b-form-input
                        id="port"
                        :state="errors.length > 0 ? false:null"
                        v-model="port"
                        :placeholder="$t('Component(RequestEmailOptions):placeholder-smtp-port')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
            </b-form-group>
        </div>
        <div class="float-right demo-inline-spacing">
            <Button class="mr-0" @click="openPopup" v-if="isConnectProviderButtonVisible">
                {{ $t('Component(RequestEmailOptions):connect-oauth-provider', {provider: emailProvider.title}) }}
            </Button>
            <Button class="mr-0" @click="sendInvitation" v-if="isInvitationFlow && !isInvitationSent">
                {{ $t('Component(RequestEmailOptions):send-invitation-for-provider', {provider: emailProvider.title}) }}
            </Button>
            <Button class="mr-0" @click="resolveAction" v-if="showNextButton">
                {{ $t('Component(Button):next') }}
            </Button>
            <Button class="mr-0" @click="validate" v-if="isValidateButtonVisible">
                {{ $t('Component(RequestEmailOptions):button-smtp-validate') }}
            </Button>
        </div>
    </Overlay>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BSpinner, BFormRadio, BAlert
} from 'bootstrap-vue'
import Overlay from "@core/components/common/Overlay";
import {Button} from "@core/components/common/index";
import {togglePasswordVisibility} from "@core/mixins";
import {SystemApi} from "@core/api";
import AttemptInquirer from "@/libs/attempt-inquirer";
import JwtService from '@/auth/jwt/useJwt'
import backendResponse from "@core/mixins/ui/backend-response";
import ProviderSelectionList from "@core/components/common/email/ProviderSelectionList";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'

export default {
    mixins: [togglePasswordVisibility, backendResponse],
    components: {
        ValidationProvider, ValidationObserver, BFormGroup, BFormInput, BInputGroupAppend, Overlay, Button, BInputGroup,
        BSpinner, ProviderSelectionList, AppCollapse, AppCollapseItem, BFormRadio, BAlert
    },
    data() {
        return {
            emailValue: '',
            password: '',
            appPassword: '',
            smtpId: null,

            emailProvider: null,
            resolvedEmailProviderEmail: null,

            type: null,

            host: '',
            port: 465,

            showPasswordField: false,
            showSmtpSettings: false,
            showOauthProvider: false,
            showProviderSelector: false,
            showAppPassword: false,
            showNextButton: true,
            showConfigurationExists: false,

            isResolvingInProgress: false,
            isValidationInProgress: false,
            isValidateButtonVisible: false,
            isConnectProviderButtonVisible: false,
            isInvitationFlow: false,
            isInvitationSent: false,

            isEmailHasChanged: false,
            isOauth: false,
            isOauthConnected: false,

            configuration: null,
            providerResolvedFinished: false,

            showOverlay: false,

            existingConfiguration: {
                id: null,
            },

            popup: null
        }
    },
    methods: {
        onEmailInput() {
            if (this.emailValue !== this.resolvedEmailProviderEmail) {
                this.isValidateButtonVisible = false;
                this.showOauthProvider = false;
                this.showNextButton = true;
                this.showProviderSelector = false;
                this.emailProvider = null;
                this.showAppPassword = false;
                this.showPasswordField = false;
                this.showSmtpSettings = false;
                this.isConnectProviderButtonVisible = false;
                this.showConfigurationExists = false;
                this.isInvitationFlow = false;
                this.isInvitationSent = false;
            }
        },
        providerConnected(username) {
            this.isValidateButtonVisible = true;
            this.showOauthProvider = false;
            this.isOauthConnected = true;
            this.showNextButton = false;
            this.isConnectProviderButtonVisible = false;
            this.emailValue = username;
        },
        setEventListener() {
            const cb = (e) => {
                if (e.data !== null && e.data.chemity && e.data.event === 'provider-connected') {
                    this.providerConnected(e.data.username);
                    window.removeEventListener('message', cb, false);
                }
            };
            window.addEventListener('message', cb, false);
        },
        openPopup() {
            const width = parseInt(window.innerWidth) * 0.3;
            const height = parseInt(window.innerHeight) * .8;
            const left = parseInt(window.innerWidth) / 2 - (width / 2);
            const top = parseInt(window.innerHeight) / 2;
            this.isOauth = true;

            this.popup = window.open(
                this.emailProvider.oauthSettings.connectUrl
                    .replace('_token_', JwtService.getToken())
                    .replace('_username_', this.emailValue),
                'MyParentWindowPopUp',
                'width=' + width + ',height=' + height + ',toolbar=0,menubar=0,location=0,status=0,scrollbars=1,resizable=0,left=' + left + ',top=' + top
            );
            this.setEventListener();
        },
        sendInvitation() {
            this.showOverlay = true;
            SystemApi.email
                .sendInvitation(
                    this.emailValue,
                    this.emailProvider.id,
                    window.location.pathname + window.location.search
                )
                .then(r => {
                    this.isInvitationSent = true;
                })
                .catch(this.handleBackendError)
                .finally(() => this.showOverlay = false)
            ;
        },
        resolveAction() {
            if (!this.showPasswordField) {
                this.resolveEmailProvider();
            } else {
                if (this.emailValue !== this.resolvedEmailProviderEmail) {
                    this
                        .resolveEmailProvider()
                        .then(() => {
                            this.validate()
                        });
                } else {
                    this.validate();
                }
            }
        },
        handleConfigurationExistsCase(configuration) {
            this.existingConfiguration = configuration;
            this.showConfigurationExists = true;
            this.showNextButton = false;

            if (false === configuration.valid) {
                if ('smtp' === configuration.type) {
                    this.showPasswordField = true;
                    this.password = '';
                }
                this.isValidateButtonVisible = true;
            }

            if (configuration.configuration.provider) {
                this.emailProvider = configuration.configuration.provider;
            }
        },
        resolveEmailProvider() {
            this.isResolvingInProgress = true;
            return new Promise((resolve, reject) => {
                this.$refs.email
                    .validate()
                    .then(isValid => {
                        if (isValid) {
                            return true;
                        }
                        reject();
                        return Promise.reject();
                    })
                    .then(() => {
                        //  check if we already have this configuration
                        SystemApi.email
                            .searchEmailConfiguration(this.emailValue)
                            .then(r => {
                                this.handleConfigurationExistsCase(r.data);
                            })
                            .catch(e => {
                                if (e && e.response && 404 === e.response.status) {
                                    this.resolveProvider(resolve, reject);
                                } else {
                                    reject();
                                    this.handleBackendError(e);
                                }
                            });
                    })
                ;
            });
        },
        resolveProvider(resolve, reject) {
            this.showOverlay = true;
            SystemApi.email
                .resolveEmailProvider(this.emailValue)
                .then(r => {
                    const attemptInquirer = new AttemptInquirer(20, 1000);
                    attemptInquirer.onComplete(() => {
                        this.resolvedEmailProviderEmail = this.emailValue;
                        this.showOverlay = false;
                        this.isResolvingInProgress = false;
                        resolve();
                    });
                    attemptInquirer.run((proceed) => {
                        SystemApi.email
                            .getResolveEmailProviderState(r.data.id)
                            .then(r => {
                                if (r.data.isFinished) {
                                    this.resolvedEmailProviderEmail = this.emailValue;
                                    this.showOverlay = false;
                                    this.isResolvingInProgress = false;
                                    this.emailProviderResolvingCompleted(r.data, resolve, reject);
                                } else {
                                    proceed();
                                }
                            })
                            .catch(() => {
                                reject();
                            })
                        ;
                    })
                })
            ;
        },
        setProvider(provider, configuration) {
            this.isOauth = false;
            this.emailProvider = provider;
            this.isConnectProviderButtonVisible = false;
            this.isValidateButtonVisible = true;
            this.showPasswordField = false;
            this.showSmtpSettings = false;
            this.showNextButton = false;

            if (false === this.emailProvider.appPasswordSettings.required) {
                this.showAppPassword = false;
                if (this.emailProvider.oauthSettings.supported) {
                    this.showOauthProvider = true;
                    this.isConnectProviderButtonVisible = true;
                    this.isValidateButtonVisible = false;
                } else if (this.emailProvider.invitationFlow) {
                    if (configuration.enabled) {
                        if (configuration.required) {
                            this.isValidateButtonVisible = false;
                            this.isInvitationFlow = true;
                        } else {
                            this.isValidateButtonVisible = true;
                        }
                    }
                } else {
                    //  we suppose it is enough email password and smtp settings
                    this.showPasswordField = true;
                }
            } else if (true === this.emailProvider.appPasswordSettings.required) {
                //  handle app password case
                this.showAppPassword = true;
            }
        },
        emailProviderResolvingCompleted(response, resolve, reject) {
            if (response.provider) {
                this.setProvider(response.provider, response.configuration);
                resolve();
            } else {
                //  can not resolve provider
                this.showProviderSelector = true;
                this.showNextButton = false;
            }
        },
        onProviderSelect({isProvider, isSmtp, provider}) {
            if (isProvider) {
                this.setProvider(provider, {});
            } else if (isSmtp) {
                this.emailProvider = null;
                this.enterSmtpSettings();
            }
        },
        enterSmtpSettings() {
            this.showAppPassword = false;
            this.showPasswordField = true;
            this.showSmtpSettings = true;
            this.isConnectProviderButtonVisible = false;
            this.isValidateButtonVisible = true;
        },
        validate() {
            if (this.isOauth) {
                this.doValidate();
            } else {
                this.$refs[this.showAppPassword ? 'appPassword' : 'password'].validate().then(success => {
                    if (success) {
                        this.doValidate()
                    }
                })
            }
        },
        doValidate() {
            this.showOverlay = true;
            this.isValidationInProgress = true;

            SystemApi.email
                .validateSmtp(
                    this.emailValue,
                    this.password || this.appPassword   ,
                    this.host,
                    this.port,
                    this.emailProvider,
                    this.existingConfiguration
                )
                .then(r => {
                    this.smtpId = r.data.id;
                    this.awaitValidationComplete();
                })
                .catch(e => {
                    this.validatingSmtpDetails = false;
                    this.isValidationInProgress = false;

                    this.showOverlay = false;
                    this.handleBackendError(e);
                });
        },
        awaitValidationComplete() {
            const attemptInquirer = new AttemptInquirer(10, 3000);
            attemptInquirer.onComplete(() => {
                this.isValidationInProgress = false;
                this.showOverlay = false;
            });
            attemptInquirer.run((proceed) => {
                SystemApi.email
                    .getConfiguration(this.smtpId)
                    .then(r => {
                        if (r.data.valid) {
                            this.configuration = r.data;
                            this.restoreDefaultState();
                            this.emitConfiguration();
                        } else {
                            proceed();
                        }
                    })
                    .catch(e => {
                        this.isValidationInProgress = false;
                        this.showOverlay = false;
                        this.handleBackendError(e);
                    })
                ;
            });
        },

        emitConfiguration() {
            this.$emit('configuration', this.configuration);
        },

        restoreDefaultState() {
            this.isValidationInProgress = false;
            this.showOverlay = false;
            this.emailValue = '';
            this.password = '';
            this.showPasswordField = false;
            this.showOauthProvider = false;
            this.showNextButton = true;
            this.isOauth = false;
            this.isConnectProviderButtonVisible = false;
            this.showAppPassword = false;
            this.isValidateButtonVisible = false;
            this.showConfigurationExists = false;
            this.isInvitationFlow = false;
            this.isInvitationSent = false;
        }
    },

    computed: {
        passwordToggleIcon() {
            return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
        },
        overlayMessage() {
            if (this.isResolvingInProgress) {
                return this.$t('Component(ConfigureEmailProvider):resolving-email-provider');
            } else if (this.isValidationInProgress) {
                return this.$t('Component(ConfigureEmailProvider):validating-email-settings');
            } else if (this.isInvitationFlow) {
                return this.$t('Component(ConfigureEmailProvider):sending-invitation');
            } else {
                return '';
            }
        }
    }
}
</script>
